import * as asyncValue from './asyncValue';
import type { AsyncValue } from './asyncValue';

export type AsyncResponseValue<Type, Meta = never> = {
  response: Type | null;
  meta: Meta | null;
};

/**
 * A value that is populated asynchronously.
 *
 * This is a wrapper around the AsyncValue type that includes a meta field. This is too accommodate
 * for async responses/redux responses.
 *
 * The modified AsyncResponse has similar function signatures as the AsyncValue, but adds a meta parameter
 * in the pending and resolve functions.
 *
 * Under the hood, this is modifying the `value` of the AsyncValue to an object with a response and meta field.
 *
 * @deprecated - Prefer the `useQuery`/`useMutation` hooks from `./app/ReactQuery/` instead. Do not add new usages.
 */
export type AsyncResponse<Type, Meta = never> = AsyncValue<AsyncResponseValue<Type, Meta>>;

/** Creates an empty AsyncValue. */
export function empty(): AsyncResponse<never> {
  return asyncValue.empty();
}

/** Creates a pending AsyncValue with no data and an optional meta. */
export function pending<Type, Meta>(meta: Meta | null = null): AsyncResponse<Type, Meta> {
  return asyncValue.setPending(
    asyncValue.resolve({ response: null, meta }),
  );
}

/** Creates an AsyncValue with the given data. */
export function resolve<Type, Meta>(value: Type, meta: Meta | null = null): AsyncResponse<Type, Meta> {
  return asyncValue.resolve({ response: value, meta });
}

/** Creates an errored AsyncValue with no data. */
export function reject(errors: string[]): AsyncResponse<never> {
  return asyncValue.reject(errors);
}

/** Returns a new `AsyncValue` that is pending with the same value */
export function setPending<Type, Meta>(value: AsyncResponse<Type, Meta>): AsyncResponse<Type, Meta> {
  return asyncValue.setPending(value);
}
